import axios from 'axios';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../utils/config';
import type {
  Activity,
  AppUser,
  AppUserSummary,
  Note,
  Page,
  SendResult,
  Thread,
} from './types';

export type {
  Activity,
  AppUser,
  AppUserSummary,
  Note,
  Page,
  SendResult,
  Thread,
} from './types';

const base = `${config.firebaseApiUrl}/fb/homeowners`;

export function lookup(value: string): {
  key: 'email' | 'uid' | 'device';
  label: string;
} {
  if (value.includes('@')) return { key: 'email', label: 'email' };
  if (/^\d+$/.test(value))
    return { key: 'device', label: 'Clip device number' };
  if (/^[a-f\d]{24}$/i.test(value))
    return { key: 'device', label: 'Clip serial' };
  return { key: 'uid', label: 'account UID' };
}
export const category = (type: Note['type']): string =>
  ({
    systemChanges: 'System changes',
    systemIssue: 'System issue',
    feedback: 'Feedback',
  })[type] ?? type;
export const identity = (
  appUser: Pick<AppUserSummary, 'uid' | 'email'>,
): string => appUser.email || appUser.uid;
export const activityIdentity = (
  item: Pick<Activity, 'uid' | 'email' | 'name'>,
): string => item.name || item.email || item.uid;
export const noteText = (note: Note): string =>
  [note.systemText, note.userText].filter(Boolean).join('\n\n') || 'No text';
export const excerpt = (value: string): string =>
  value.length > 140 ? `${value.slice(0, 140)}…` : value;
export function apiMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (data && typeof data === 'object' && 'message' in data) {
      const message = String(data.message);
      return 'code' in data ? `${String(data.code)}: ${message}` : message;
    }
  }
  return 'The request failed. Please try again.';
}
export function apiCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  return data && typeof data === 'object' && 'code' in data
    ? String(data.code)
    : null;
}
export const appUserApi = {
  search: async (value: string) =>
    (
      await axiosInstance.get<AppUserSummary[]>(`${base}/search`, {
        params: { [lookup(value).key]: value },
      })
    ).data,
  activity: async (cursor?: string) =>
    (
      await axiosInstance.get<Page<Activity>>(`${base}/notes/activity`, {
        params: cursor ? { cursor } : undefined,
      })
    ).data,
  overview: async (uid: string) =>
    (await axiosInstance.get<AppUser>(`${base}/${encodeURIComponent(uid)}`))
      .data,
  notes: async (uid: string, cursor?: string) =>
    (
      await axiosInstance.get<Page<Note>>(
        `${base}/${encodeURIComponent(uid)}/notes`,
        { params: cursor ? { cursor } : undefined },
      )
    ).data,
  thread: async (uid: string, noteId: string) =>
    (
      await axiosInstance.get<Thread>(
        `${base}/${encodeURIComponent(uid)}/notes/${encodeURIComponent(noteId)}`,
      )
    ).data,
  markRead: async (uid: string, noteId: string, throughMessageId: string) =>
    (await axiosInstance.post<{ unread: boolean }>(
      `${base}/${encodeURIComponent(uid)}/notes/${encodeURIComponent(noteId)}/read`,
      { throughMessageId },
    )).data,
  send: async (uid: string, noteId: string, text: string, requestId: string) =>
    (
      await axiosInstance.post<SendResult>(
        `${base}/${encodeURIComponent(uid)}/notes/${encodeURIComponent(noteId)}/responses`,
        { text, requestId },
      )
    ).data,
};
